import React, { useMemo } from 'react';
import { Award } from 'lucide-react';
import { CompactError, RobbieOverlay, StepBar } from '../ui';
import { useQuoteRecommendation } from '../../hooks/useQuoteRecommendation';
import { useAwardDraft } from '../../hooks/useAwardDraft';
import { computeJourneyReadiness } from '../../utils/journeyReadiness';
import { useReportStageActivity } from '../../hooks/useStageActivity';
import { StageReport, failedStage } from '../../utils/stageActivity';
import ComponentAwardBuilder from './quotes/ComponentAwardBuilder';
import FinalDeliverablesCard from './quotes/FinalDeliverablesCard';
import ReadinessStrip from './quotes/ReadinessStrip';
import RecommendationEmptyState from './quotes/RecommendationEmptyState';
import RecommendationSkeleton from './quotes/RecommendationSkeleton';
import StrategyBar from './quotes/StrategyBar';
import AwardPlanStatus from './quotes/AwardPlanStatus';
import { useReadOnly } from '../../contexts/WizardAccessContext';
import { quotesCopy } from '../../utils/waitCopy';

interface QuotesProposalsProps {
    rfq: any;
    onBack: () => void;
    /** Tells the wizard this step is mid-run, so Robbie drops the suggestion. */
    onWorking?: (stage: StageReport | null) => void;
}

const RECOMMENDATION_FAILED = "I couldn't finish the award recommendation";

const plural = (n: number) => `${n} supplier${n === 1 ? '' : 's'}`;

const QuotesProposals: React.FC<QuotesProposalsProps> = ({ rfq, onBack, onWorking }) => {
    // Same data-truth the journey rail reads, so the story matches across screens.
    const readiness = useMemo(() => computeJourneyReadiness(rfq), [rfq]);
    const estimateOnly = readiness.quotedCount === 0;
    const supplierCount = readiness.quotedCount > 0 ? readiness.quotedCount : readiness.shortlistedCount;
    const {
        result, recommendation, componentContext, awardPlan, isAccepted,
        isLoading, isGenerating, isLong, runError, dismissRunError, isAccepting, downloading, error,
        dismissError, generate, accept, download, downloadSupplierRfq,
    } = useQuoteRecommendation(rfq?.rfqId, quotesCopy.longAfterMs(supplierCount));
    const draft = useAwardDraft(result, componentContext, recommendation, awardPlan);

    const readOnly = useReadOnly();

    // The overlay covers the step while it runs — nothing to suggest meanwhile.
    useReportStageActivity(
        onWorking,
        isGenerating ? 'recommendation' : runError ? failedStage('recommendation', RECOMMENDATION_FAILED) : null,
    );

    const supplierNames = useMemo(() => Array.from(new Set(
        componentContext.flatMap(c => c.candidates.map(candidate => candidate.supplierName))
    )), [componentContext]);

    const hasPlan = Boolean(recommendation || awardPlan);
    const ctaNote = readOnly ? undefined
        : !hasPlan ? undefined
        : !draft.allComplete ? "Each component's allocation must total 100%."
        : isAccepted && draft.draftMatchesConfirmed ? 'Award plan confirmed.'
        : 'Plan ready — every component totals 100%.';

    const overlayError = runError ? (
        <CompactError
            title={RECOMMENDATION_FAILED}
            message={runError}
            action={{ label: 'Try again', onClick: generate }}
            secondaryAction={{ label: 'Dismiss', onClick: dismissRunError }}
        />
    ) : undefined;

    const renderBody = () => {
        if (isLoading || (isGenerating && !hasPlan)) return <RecommendationSkeleton />;
        if (!hasPlan) {
            return (
                <>
                    <ReadinessStrip readiness={readiness} />
                    <RecommendationEmptyState readOnly={readOnly} estimateOnly={estimateOnly} onGenerate={generate} />
                </>
            );
        }
        return (
            <>
                <ReadinessStrip readiness={readiness} />
                {recommendation && (
                    <StrategyBar
                        recommendation={recommendation}
                        awardPlan={awardPlan}
                        isAccepted={isAccepted}
                        readOnly={readOnly}
                        busy={isGenerating || isAccepting}
                        onRegenerate={generate}
                    />
                )}
                <div className="flex flex-col gap-s4">
                    {draft.awardable.map((component, idx) => (
                        <ComponentAwardBuilder
                            key={component.componentId}
                            component={component}
                            aiRecommendation={recommendation?.components.find(c => c.componentId === component.componentId)}
                            allocations={draft.draftPlans[component.componentId] || []}
                            onChangeShare={(supplierName, newShare) => draft.setShare(component.componentId, supplierName, newShare)}
                            onResetToAI={() => draft.resetToAI(component.componentId)}
                            index={idx}
                            disabled={isAccepting}
                            readOnly={readOnly}
                        />
                    ))}
                </div>
                <AwardPlanStatus estimateOnly={estimateOnly} isAccepted={isAccepted} handoffEmails={result?.handoffEmails} />
                <FinalDeliverablesCard
                    isUnlocked={isAccepted}
                    downloading={downloading}
                    onDownload={download}
                    awardedSuppliers={draft.awardedSuppliers}
                    onDownloadSupplierRfq={downloadSupplierRfq}
                />
            </>
        );
    };

    return (
        <div className="flex flex-col font-sans">
            <div className="relative flex min-h-[420px] flex-col gap-s5 pb-s7">
                {error && (
                    <CompactError
                        layout="row"
                        title="That didn't go through"
                        message={error}
                        secondaryAction={{ label: 'Dismiss', onClick: dismissError }}
                    />
                )}
                {renderBody()}
                <RobbieOverlay
                    open={isGenerating || Boolean(runError)}
                    title={`Comparing quotes from ${plural(supplierCount)}`}
                    sub={supplierNames.length > 0 ? supplierNames.join(' · ') : undefined}
                    usual={quotesCopy.usual(supplierCount)}
                    long={isLong}
                    leave
                    error={overlayError}
                />
            </div>
            <StepBar
                stepLabel="Step 5 · Quotes"
                summary={`Quotes from ${plural(supplierCount)}${readOnly && isAccepted ? ' · award plan confirmed' : ''}`}
                onBack={onBack}
                ctaNote={ctaNote}
                // A viewer can't confirm, so the bar is the summary alone.
                cta={readOnly ? undefined : {
                    label: isAccepted && !draft.draftMatchesConfirmed ? 'Update award' : 'Confirm award',
                    icon: <Award />,
                    onClick: () => accept(draft.planComponents()),
                    loading: isAccepting,
                    loadingLabel: 'Confirming…',
                    disabled: !hasPlan || isGenerating || !draft.allComplete
                        || (isAccepted && draft.draftMatchesConfirmed),
                }}
            />
        </div>
    );
};

export default QuotesProposals;
